import { ACTION_STATES } from "../combat/anim.ts";
import { fieldsOf, identifier, listOf, listed, pathTo, report, ticks, whole, type Place } from "./checks.ts";
import type { AbilityShape, Chain, ChainHit, Combo, Damage } from "./types.ts";

/**
 * An ability's `combo` and `chain` (ADR-073's amendment "the Strike combo and the Heavy Strike chain", item 1): the strips
 * its consecutive uses alternate, and the blows one act of it strikes. Each strip is an action strip, the first the
 * ability's own; a combo holds 2 to 4, the steps `used` carries in two bits; a chain holds 2 to 8 blows, the first at 0
 * and each later one after the one before, whose amounts sum to the ability's damage and whose root lies after the last
 * blow and within the cooldown. Only a cone that deals damage holds a chain, and no ability holds both.
 */

const COMBO_FIELDS = ["strips", "graceTicks"] as const;
const CHAIN_FIELDS = ["hits", "rootTicks"] as const;
const HIT_FIELDS = ["atTicks", "strip", "amount"] as const;
const MIN_COMBO_STRIPS = 2;
const MAX_COMBO_STRIPS = 4;
const MIN_CHAIN_HITS = 2;
const MAX_CHAIN_HITS = 8;
const MAX_GRACE_TICKS = 0xffff;

/**
 * Checks a strip: an id's form, naming an action state a character plays (ADR-074 decision 7).
 *
 * @param place Where to report.
 * @param value The strip.
 * @param path Its path.
 * @returns The strip, or null.
 */
export function actionStrip(place: Place, value: unknown, path: string): string | null {
  const strip = identifier(place, value, path);
  if (strip !== null && !ACTION_STATES.has(strip)) {
    report(place, `strip ${strip} has no action state (${listed([...ACTION_STATES.keys()])})`);
    return null;
  }
  return strip;
}

/**
 * Checks a combo: null for none, or 2 to 4 action strips starting with the ability's strip and a grace of whole ticks.
 *
 * @param place Where to report.
 * @param value The combo.
 * @param strip The ability's strip, null when it was wrong.
 * @returns The combo, null for none, or undefined when it is wrong.
 */
export function comboOf(place: Place, value: unknown, strip: string | null): Combo | null | undefined {
  if (value === null) {
    return null;
  }
  const fields = fieldsOf(place, value, "combo", COMBO_FIELDS, "a combo");
  if (fields === null) {
    return undefined;
  }
  const list = listOf(place, fields["strips"], "combo.strips");
  const graceTicks = ticks(place, fields["graceTicks"], "combo.graceTicks", 0, MAX_GRACE_TICKS);
  if (list === null) {
    return undefined;
  }
  let ok = graceTicks !== null && Object.keys(fields).length === COMBO_FIELDS.length;
  if (list.length < MIN_COMBO_STRIPS || list.length > MAX_COMBO_STRIPS) {
    report(place, `combo.strips holds ${list.length} strip${list.length === 1 ? "" : "s"}, and a combo holds ${MIN_COMBO_STRIPS} to ${MAX_COMBO_STRIPS}`);
    ok = false;
  }
  const strips: string[] = [];
  for (const [index, item] of list.entries()) {
    const checked = actionStrip(place, item, pathTo("combo.strips", index, true));
    if (checked === null) {
      ok = false;
    } else {
      strips.push(checked);
    }
  }
  if (strip !== null && strips.length > 0 && strips[0] !== strip) {
    report(place, `combo.strips starts with ${strips[0] as string}, not the ability's strip ${strip}`);
    ok = false;
  }
  return ok ? { strips, graceTicks: graceTicks as number } : undefined;
}

/**
 * Checks one blow of a chain.
 *
 * @param place Where to report.
 * @param value The blow.
 * @param index Its place in the chain.
 * @returns The blow, or null when it is wrong.
 */
function chainHitOf(place: Place, value: unknown, index: number): ChainHit | null {
  const path = pathTo("chain.hits", index, true);
  const fields = fieldsOf(place, value, path, HIT_FIELDS, "a chain's hit");
  if (fields === null) {
    if (value === undefined) {
      report(place, `${path} is missing`);
    }
    return null;
  }
  const atTicks = ticks(place, fields["atTicks"], `${path}.atTicks`, 0);
  const strip = actionStrip(place, fields["strip"], `${path}.strip`);
  const amount = whole(place, fields["amount"], `${path}.amount`, 1);
  if (atTicks === null || strip === null || amount === null || Object.keys(fields).length !== HIT_FIELDS.length) {
    return null;
  }
  return { atTicks, strip, amount };
}

/**
 * Checks a chain's blows against one another and the ability: the first at 0 with the ability's strip, each later one
 * after the one before.
 *
 * @param place Where to report.
 * @param hits The blows, in order.
 * @param strip The ability's strip, null when it was wrong.
 * @returns True when they are in order.
 */
function inOrder(place: Place, hits: readonly ChainHit[], strip: string | null): boolean {
  let ok = true;
  const first = hits[0] as ChainHit;
  if (first.atTicks !== 0) {
    report(place, `chain.hits[0].atTicks is ${first.atTicks}, and the first hit is at 0`);
    ok = false;
  }
  if (strip !== null && first.strip !== strip) {
    report(place, `chain.hits[0].strip is ${first.strip}, not the ability's strip ${strip}`);
    ok = false;
  }
  for (let index = 1; index < hits.length; index += 1) {
    const before = (hits[index - 1] as ChainHit).atTicks;
    const at = (hits[index] as ChainHit).atTicks;
    if (at <= before) {
      report(place, `chain.hits[${index}].atTicks is ${at}, not after the hit before it at ${before}`);
      ok = false;
    }
  }
  return ok;
}

/**
 * What a chain is checked against: the ability's strip, shape, damage and cooldown, each null when it was wrong.
 */
export interface ChainContext {
  readonly strip: string | null;
  readonly shape: AbilityShape | null;
  readonly damage: Damage | null | undefined;
  readonly cooldownTicks: number | null;
}

/**
 * Checks a chain: null for none, or 2 to 8 blows in order and a root after the last within the cooldown, on a cone that
 * deals damage, the blows' amounts summing to its damage.
 *
 * @param place Where to report.
 * @param value The chain.
 * @param ability The ability's checked strip, shape, damage and cooldown.
 * @returns The chain, null for none, or undefined when it is wrong.
 */
export function chainOf(place: Place, value: unknown, ability: ChainContext): Chain | null | undefined {
  if (value === null) {
    return null;
  }
  const fields = fieldsOf(place, value, "chain", CHAIN_FIELDS, "a chain");
  if (fields === null) {
    return undefined;
  }
  const { shape, damage, cooldownTicks } = ability;
  let ok = Object.keys(fields).length === CHAIN_FIELDS.length;
  if ((shape !== null && shape.kind !== "cone") || damage === null) {
    report(place, "a chain rides a cone that deals damage");
    ok = false;
  }
  const list = listOf(place, fields["hits"], "chain.hits");
  const rootTicks = ticks(place, fields["rootTicks"], "chain.rootTicks", 1);
  if (list === null || rootTicks === null) {
    return undefined;
  }
  if (list.length < MIN_CHAIN_HITS || list.length > MAX_CHAIN_HITS) {
    report(place, `chain.hits holds ${list.length} hit${list.length === 1 ? "" : "s"}, and a chain holds ${MIN_CHAIN_HITS} to ${MAX_CHAIN_HITS}`);
    return undefined;
  }
  const hits: ChainHit[] = [];
  for (let index = 0; index < list.length; index += 1) {
    const hit = chainHitOf(place, list[index], index);
    if (hit === null) {
      ok = false;
    } else {
      hits.push(hit);
    }
  }
  if (!ok || hits.length !== list.length || !inOrder(place, hits, ability.strip)) {
    return undefined;
  }
  const last = (hits[hits.length - 1] as ChainHit).atTicks;
  if (rootTicks <= last) {
    report(place, `chain.rootTicks is ${rootTicks}, not after the last hit at ${last}`);
    ok = false;
  }
  if (cooldownTicks !== null && rootTicks > cooldownTicks) {
    report(place, `chain.rootTicks is ${rootTicks}, past the cooldown of ${cooldownTicks}`);
    ok = false;
  }
  const sum = hits.reduce((total, hit) => total + hit.amount, 0);
  if (damage !== undefined && damage !== null && sum !== damage.amount) {
    report(place, `the chain's amounts sum to ${sum}, not damage.amount ${damage.amount}`);
    ok = false;
  }
  return ok ? { hits, rootTicks } : undefined;
}

/**
 * Checks that an ability holds a combo or a chain, never both.
 *
 * @param place Where to report.
 * @param combo Its combo.
 * @param chain Its chain.
 * @returns True when it holds one at most.
 */
export function oneOfComboOrChain(place: Place, combo: Combo | null | undefined, chain: Chain | null | undefined): boolean {
  if (combo !== null && combo !== undefined && chain !== null && chain !== undefined) {
    report(place, "holds a combo and a chain, and an ability holds one at most");
    return false;
  }
  return true;
}
